

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../../../pages/style.css";
import "../../../pages/responsive.css";
import fetchDataWithAuth from "../../../utils/api";
import { getLocalStorageItem } from "../../../utils/HelperFunctions";
import { showUpdationModal } from "../../../utils/Helpers";
import { validateEmail, getNameValidationMessage, validatePhoneNumber } from "../../../utils/HelperFunctions";
import { encryptUserId, decryptUserId } from "../../../utils/cryptoUtils";

const MyInfoModal = ({ setEditMode, setShowchangePassword, handleMyInfoClose, Email, setEmail }) => {
    const { t } = useTranslation();
    const [error, setError] = useState({});
    const [successMsg, setSuccessMsg] = useState('');
    const [isEditMode, setIsEditModeLocal] = useState(false);
    const [displayUserData, setDisplayUserData] = useState({
        Id: getLocalStorageItem('UserId'),
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
        organization: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postalCode: ''
    });
    const [formUserData, setFormUserData] = useState({
        Id: getLocalStorageItem('UserId'),
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
        organization: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postalCode: ''
    });

    const [isCtrlFocused, setIsCtrlFocused] = useState({
        username: false,
        password: false,
        firstName: false,
        lastName: false,
        email: false,
        jobTitle: false,
        organization: false,
        // Add other fields here as needed
    });

    const [isCtrlEmpty, setIsCtrlEmpty] = useState({
        username: false,
        password: false,
        firstName: false,
        lastName: false,
        email: false,
        jobTitle: false,
        organization: false,
        // Add other fields here as needed
    });

    useEffect(() => {
        setEditMode(isEditMode);
    }, [isEditMode, setEditMode]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userID = getLocalStorageItem('UserId');
                if (!userID) {
                    setError({ general: t('User ID is not available') });
                    return;
                }

                //const data = await fetchDataWithAuth(`/User/${userID}`, "GET");
                const data = await fetchDataWithAuth(`/User/webapp/${userID}`, "GET");

                const decryptUserName = decryptUserId(data.username);
                const decryptEmail = decryptUserId(data.email);
                const decryptPhone = decryptUserId(data.phone);
                data.username = decryptUserName;
                data.email = decryptEmail;
                data.phone = decryptPhone;

                setDisplayUserData(prevData => ({
                    ...prevData,
                    ...data
                }));
                setFormUserData(prevData => ({
                    ...prevData,
                    ...data
                }));
                setEmail(data.email);
            } catch (error) {
                setError({ general: t('Error fetching user data') });
            }
        };

        fetchUserData();
    }, [t, setEmail]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormUserData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleFocus = (e) => {
        const name = e.target.name;
        setIsCtrlFocused((prevState) => ({
            ...prevState,
            [name]: true,  // Set focus to true for the current input
        }));
    };

    const handleBlur = (e) => {
        const name = e.target.name;
        setIsCtrlFocused((prevState) => ({
            ...prevState,
            [name]: false,  // Set focus to false when input loses focus
        }));
        setIsCtrlEmpty((prevState) => ({
            ...prevState,
            [name]: e.target.value.trim() === "", // Check if the input is empty after blur
        }));
    };

    const handleChangePassword = () => {
        setShowchangePassword(true);
        setIsEditModeLocal(false);
        handleMyInfoClose(true);
    };

    const validate = () => {
        const newErrors = {};
        const firstNameValidation = getNameValidationMessage(
            t,
            formUserData.firstName,
            t("invalidNameValidation")
        );
        if (firstNameValidation) {
            newErrors.firstName = firstNameValidation;
        }
        const lastNameValidation = getNameValidationMessage(
            t,
            formUserData.lastName,
            t("invalidNameValidation")
        );
        if (lastNameValidation) {
            newErrors.lastName = lastNameValidation;
        }
        const jobTitleValidation = getNameValidationMessage(
            t,
            formUserData.jobTitle,
            t("invalidNameValidation")
        );
        if (jobTitleValidation) {
            newErrors.jobTitle = jobTitleValidation;
        }
        const organizationValidation = getNameValidationMessage(
            t,
            formUserData.organization,
            t("invalidNameValidation")
        );
        if (organizationValidation) {
            newErrors.organization = organizationValidation;
        }
        const cityValidation = getNameValidationMessage(
            t,
            formUserData.city,
            t("invalidNameValidation"),
            false
        );
        if (cityValidation) {
            newErrors.city = cityValidation;
        }
        const stateValidation = getNameValidationMessage(
            t,
            formUserData.state,
            t("invalidNameValidation"),
            false
        );
        if (stateValidation) {
            newErrors.state = stateValidation;
        }

        if (/[^0-9]/.test(formUserData.postalCode)) {
            newErrors.postalCode = `${t("Invalid input. Only numbers are allowed")}`;
        }
        const phoneValidation = validatePhoneNumber(
            t,
            formUserData.phone,
            false
        );
        if (phoneValidation) {
            newErrors.phone = phoneValidation;
        }
        setError(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const token = getLocalStorageItem('token');

        // const encryptedUserName = encryptUserId(formUserData.username);
        // const encryptedEmail = encryptUserId(formUserData.email);
        const encryptedPhoneNumber = encryptUserId(formUserData.phone);

        // const updatedData = {
        //     ...formUserData,
        //     Id: formUserData.Id,
        //     username: encryptedUserName,
        //     email: encryptedEmail,
        //     phone: encryptedPhoneNumber
        // };

        const updatedData={
            phone: encryptedPhoneNumber,
            firstName: formUserData.firstName,
            lastName: formUserData.lastName,
            jobTitle: formUserData.jobTitle,
            organization: formUserData.organization,
            address: formUserData.address,
            city: formUserData.city,
            state: formUserData.state,
            postalCode: formUserData.postalCode,
        }

        try { 
            const response = await fetch(
                //`${process.env.REACT_APP_API_URL}/User/${formUserData.Id}`,
                `${process.env.REACT_APP_API_URL}/User/UpdateUserWeb`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(updatedData)
                });

            if (response.ok) {
                // setSuccessMsg(t('Update successful'));
                showUpdationModal(t('Update successful'), 'success')
                setTimeout(() => setSuccessMsg(''), 3000);
                // Update display data with the form data if submission is successful

                // const decryptUserName = decryptUserId(updatedData.username);
                // const decryptEmail = decryptUserId(updatedData.email);
                const decryptPhone = decryptUserId(updatedData.phone);

                // updatedData.username = decryptUserName;
                // updatedData.email = decryptEmail;
                updatedData.phone = decryptPhone;
                updatedData.username = formUserData.username;
                updatedData.email = formUserData.email;

                setDisplayUserData(updatedData);
                setIsEditModeLocal(false);
                localStorage.setItem("username", formUserData.firstName + ' ' + formUserData.lastName);
            } else {
                const errorText = await response.text();
                throw new Error(`${response.status}: ${errorText}`);
            }
        } catch (error) {
            setError({ general: 'Error updating user data' });
            showUpdationModal('Failed to update', error)
        }
    };

    const handleCancel = () => {
        setIsEditModeLocal(false);
        setError({});
        setFormUserData(displayUserData); // Reset form data to display data on cancel
    };

    return (
        <div>
            {!isEditMode ? (
                <div className="myinfoModal-box">
                    <hr className="mt-1 mb-3" />
                    <div className="row mt-0 info-heading">
                        <div className="col-sm-12 name-text omnes-medium-font">{displayUserData.username}</div>
                        <div className="col-sm-12 email-text omnes-regular-font">{displayUserData.email}</div>
                    </div>
                    <div className="btn-container myinfo-btn mt-3 mb-3">
                        <button className="btn btn-primary Lr-3" onClick={() => setIsEditModeLocal(true)}>{t("EditInfo")}</button>
                        <button className="btn btn-outline " onClick={handleChangePassword}>{t("changePassword")}</button>
                    </div>
                    {error.general && <div className="error-message" style={{ color: "red" }}>{error.general}</div>}
                    <div className="basicinfo-section mb-3">
                        <div className="info-header omnes-medium-font">{t('Basic Information')}</div>
                        <hr className="mt-2 mb-3" />
                        <div className="info-content row">
                            <div className="col-md-6 col-lg-3"><label>{t('firstName')}</label><p className="omnes-medium-font">{displayUserData.firstName}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('lastName')}</label><p className="omnes-medium-font">{displayUserData.lastName}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('Organization')}</label><p className="omnes-medium-font">{displayUserData.organization}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('jobTitle')}</label><p className="omnes-medium-font">{displayUserData.jobTitle}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('phNum')}</label><p className="omnes-medium-font">{displayUserData.phone}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('emailId')}</label><p className="omnes-medium-font breakLargeWord">{displayUserData.email}</p></div>
                        </div>
                    </div>
                    <div className="basicinfo-section">
                        <div className="info-header omnes-medium-font">{t('Address Information')}</div>
                        <hr className="mt-2 mb-3" />
                        <div className="info-content row">
                            <div className="col-md-6 col-lg-3"><label>{t('address')}</label><p className="omnes-medium-font">{displayUserData.address}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('city')}</label><p className="omnes-medium-font">{displayUserData.city}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('country')}</label><p className="omnes-medium-font">{displayUserData.state}</p></div>
                            <div className="col-md-6 col-lg-3"><label>{t('postalCode')}</label><p className="omnes-medium-font">{displayUserData.postalCode}</p></div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="editinfo-modal signup-modal">
                    <hr className="mt-1 mb-3" />
                    <form className="mt-0" onSubmit={handleSubmit}>
                        {error.general && <div className="error-message" style={{ color: "red" }}>{error.general}</div>}
                        {successMsg && <div className="success-msg" style={{ color: "green" }}>{successMsg}</div>}
                        <div className="row">
                            <div className="col-md-6 mb-3">

                                <div className={`input-wrapper ${isCtrlEmpty.username && !isCtrlFocused.username ? "show-asterisk" : ""}`} required>
                                    <input type="text" className="form-control" placeholder={t('username')} name="username" value={formUserData.username}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        disabled />
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">

                                <div className={`input-wrapper ${isCtrlEmpty.email && !isCtrlFocused.email ? "show-asterisk" : ""}`} required>
                                    <input type="text" className="form-control" name="email" placeholder={t('email')} value={formUserData.email}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                        disabled />
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">

                                <div className={`input-wrapper ${isCtrlEmpty.firstName && !isCtrlFocused.firstName ? "show-asterisk" : ""}`} required>
                                    <input type="text" className="form-control" name="firstName" placeholder={t('firstName')} value={formUserData.firstName}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                    />
                                    {error.firstName && <div className="error-message">{error.firstName}</div>}
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">

                                <div className={`input-wrapper ${isCtrlEmpty.lastName && !isCtrlFocused.lastName ? "show-asterisk" : ""}`} required>
                                    <input type="text" className="form-control" name="lastName" placeholder={t('lastName')} value={formUserData.lastName}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                    />
                                    {error.lastName && <div className="error-message">{error.lastName}</div>}
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">

                                <div className={`input-wrapper ${isCtrlEmpty.jobTitle && !isCtrlFocused.jobTitle ? "show-asterisk" : ""}`} required>
                                    <input type="text" className="form-control" name="jobTitle" placeholder={t('jobTitle')} value={formUserData.jobTitle}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                    />
                                    {error.jobTitle && <div className="error-message">{error.jobTitle}</div>}
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">

                                <div className={`input-wrapper ${isCtrlEmpty.organization && !isCtrlFocused.organization ? "show-asterisk" : ""}`} required>
                                    <input type="text" className="form-control" name="organization" placeholder={t('organization')} value={formUserData.organization}
                                        onChange={handleChange}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                    />
                                    {error.organization && <div className="error-message">{error.organization}</div>}
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">

                                <input type="text" className="form-control" name="phone" value={formUserData.phone} placeholder={t('phNum')} onChange={handleChange} />
                                {error.phone && <div className="error-message">{error.phone}</div>}
                            </div>
                            <div className="col-md-6 mb-3">

                                <input type="text" className="form-control" name="address" value={formUserData.address} placeholder={t('address')} maxLength={500} onChange={handleChange} />
                                {error.address && <div className="error-message">{error.address}</div>}
                            </div>
                            <div className="col-md-6 mb-3">

                                <input type="text" className="form-control" name="city" placeholder={t('city')} value={formUserData.city} onChange={handleChange} />
                                {error.city && <div className="error-message">{error.city}</div>}
                            </div>
                            <div className="col-md-6 mb-3">

                                <input type="text" className="form-control" name="state" placeholder={t('country')} value={formUserData.state} onChange={handleChange} />
                                {error.state && <div className="error-message">{error.state}</div>}
                            </div>
                            <div className="col-md-6 mb-3">

                                <input type="text" className="form-control" name="postalCode" placeholder={t('postalCode')} value={formUserData.postalCode} onChange={handleChange} />
                                {error.postalCode && <div className="error-message">{error.postalCode}</div>}
                            </div>
                        </div>
                        <div className="btn-container editinfo-btns  mb-2">
                            <button type="button" className="btn btn-outline Lr-3" onClick={handleCancel}>{t('cancel')}</button>
                            <button type="submit" className="btn btn-primary text-white ">{t('UpdateButton')}</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default MyInfoModal;
